(() => {
  const STORAGE_KEY = 's974:wishlist:v1';
  const MAX_ITEMS = 50;

  const config = () => window.s974Wishlist || {};

  const safeParse = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  };

  const getItems = () => {
    try {
      const items = safeParse(window.localStorage.getItem(STORAGE_KEY), []);
      if (!Array.isArray(items)) return [];
      return [...new Set(items.filter((item) => typeof item === 'string' && item.trim()))].slice(0, MAX_ITEMS);
    } catch (_error) {
      return [];
    }
  };

  const saveItems = (items) => {
    const normalized = [...new Set(items)].slice(0, MAX_ITEMS);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (_error) {
      // Wishlist remains usable in-memory for the current interaction.
    }
    document.dispatchEvent(new CustomEvent('s974:wishlist-changed', { detail: { items: normalized } }));
    return normalized;
  };

  const hasItem = (handle) => getItems().includes(handle);

  const setItem = (handle, shouldSave) => {
    const items = getItems().filter((item) => item !== handle);
    if (shouldSave) items.unshift(handle);
    return saveItems(items);
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
    if (Number.isNaN(Number(number))) return '0';
    const parts = (Number(number) / 100).toFixed(precision).split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
    return parts[1] ? parts.join(decimal) : parts[0];
  };

  const formatMoney = (cents) => {
    const moneyFormat = config().moneyFormat || '${{amount}}';
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return formatWithDelimiters(cents);

    let value;
    switch (match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return moneyFormat.replace(match[0], value);
  };

    const syncCount = (items = getItems()) => {
    document.querySelectorAll('[data-s974-wishlist-count]').forEach((element) => {
      element.textContent = String(items.length);
      element.hidden = items.length === 0;
    });
  };

  const syncButtons = (items = getItems(), scope = document) => {
    scope.querySelectorAll('[data-s974-wishlist-toggle]').forEach((button) => {
      const active = items.includes(button.dataset.productHandle);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-label', active ? config().labels?.remove : config().labels?.add);
      button.setAttribute('title', active ? config().labels?.remove : config().labels?.add);
      const label = button.querySelector('[data-s974-wishlist-label]');
      if (label) label.textContent = active ? config().labels?.remove : config().labels?.add;
    });
  };

  const handleToggle = (button) => {
    const handle = button.dataset.productHandle;
    if (!handle) return;
    const items = setItem(handle, !hasItem(handle));
    syncButtons(items);
    syncCount(items);
  };

  const productUrl = (handle) => {
    const root = config().rootUrl || '/';
    return `${root}${root.endsWith('/') ? '' : '/'}products/${encodeURIComponent(handle)}`;
  };

  const productJsonUrl = (handle) => `${productUrl(handle)}.js`;

  const imageUrl = (source) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', '720');
      return url.toString();
    } catch (_error) {
      return source;
    }
  };

  const createCard = (product, ratio) => {
    const handle = product.handle;
    const href = productUrl(handle);
    const compareAtPrice = Number(product.compare_at_price || 0);
    const price = Number(product.price || 0);
    const hasDiscount = compareAtPrice > price;
    const soldOut = product.available === false;
    const title = escapeHtml(product.title);
    const image = imageUrl(product.featured_image);
    const labels = config().labels || {};

    const article = document.createElement('article');
    article.className = 's974-wishlist-card';
    article.dataset.productHandle = handle;
    article.innerHTML = `
      <a class="s974-wishlist-card__media" href="${href}" style="--s974-wishlist-card-ratio:${ratio}">
        ${image ? `<img class="s974-wishlist-card__image" src="${escapeHtml(image)}" alt="${title}" loading="lazy" width="720" height="900">` : ''}
      </a>
      <button type="button" class="s974-wishlist-card__remove" data-s974-wishlist-remove="${escapeHtml(handle)}" aria-label="${escapeHtml(labels.remove || 'Remove from wishlist')}" title="${escapeHtml(labels.remove || 'Remove from wishlist')}">
        <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z"/></svg>
      </button>
      <div class="s974-wishlist-card__body">
        <h2 class="s974-wishlist-card__title"><a href="${href}">${title}</a></h2>
        <div class="s974-wishlist-card__price">
          <span>${formatMoney(price)}</span>
          ${hasDiscount ? `<span class="s974-wishlist-card__compare">${formatMoney(compareAtPrice)}</span>` : ''}
        </div>
        ${soldOut ? `<p class="s974-wishlist-card__sold-out">${escapeHtml(labels.soldOut || 'Sold out')}</p>` : ''}
        <a class="button button--secondary s974-wishlist-card__button" href="${href}">${escapeHtml(labels.viewProduct || 'View product')}</a>
      </div>
    `;
    return article;
  };

  const renderWishlistPage = async (section) => {
    const grid = section.querySelector('[data-s974-wishlist-grid]');
    const empty = section.querySelector('[data-s974-wishlist-empty]');
    const status = section.querySelector('[data-s974-wishlist-status]');
    if (!grid || !empty) return;

    const items = getItems();
    const ratio = section.dataset.imageRatio === 'square' ? '1' : section.dataset.imageRatio === 'adapt' ? '.82' : '.8';
    grid.replaceChildren();

    if (!items.length) {
      empty.hidden = false;
      grid.hidden = true;
      if (status) status.textContent = '';
      return;
    }

    empty.hidden = true;
    grid.hidden = false;
    if (status) status.textContent = config().labels?.loading || '';

    const products = await Promise.all(items.map(async (handle) => {
      try {
        const response = await fetch(productJsonUrl(handle), { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (_error) {
        return null;
      }
    }));

    const validProducts = products.filter(Boolean);
    const validHandles = new Set(validProducts.map((product) => product.handle));
    const cleanItems = items.filter((handle) => validHandles.has(handle));
    if (cleanItems.length !== items.length) saveItems(cleanItems);

    validProducts.forEach((product) => grid.appendChild(createCard(product, ratio)));

    if (status) status.textContent = '';
    if (!validProducts.length) {
      empty.hidden = false;
      grid.hidden = true;
      if (status) status.textContent = config().labels?.error || '';
    }
  };

  const init = (scope = document) => {
    const items = getItems();
    syncButtons(items, scope);
    syncCount(items);
    scope.querySelectorAll('[data-s974-wishlist-section]').forEach(renderWishlistPage);
  };

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-s974-wishlist-toggle]');
    if (toggle) {
      event.preventDefault();
      event.stopPropagation();
      handleToggle(toggle);
      return;
    }

    const remove = event.target.closest('[data-s974-wishlist-remove]');
    if (remove) {
      event.preventDefault();
      const handle = remove.dataset.s974WishlistRemove;
      const items = setItem(handle, false);
      syncButtons(items);
      syncCount(items);
      const section = remove.closest('[data-s974-wishlist-section]');
      if (section) renderWishlistPage(section);
    }
  });

  document.addEventListener('s974:wishlist-changed', (event) => {
    syncButtons(event.detail?.items || getItems());
    syncCount(event.detail?.items || getItems());
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    const items = getItems();
    syncButtons(items);
    syncCount(items);
    document.querySelectorAll('[data-s974-wishlist-section]').forEach(renderWishlistPage);
  });

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
})();
